import apiV2 from '../api/v2';
import type { TiptapDoc } from '@odo/shared-ui';

export interface PatientNote {
    id: string;
    patientId: string;
    severity: 'info' | 'warning' | 'critical';
    visibility: 'internal' | 'external';
    title: string;
    body: string;
    /** Canonical rich-text content. Null on legacy notes created before the
     *  rich-editor migration — render `body` (plain text) as a fallback. */
    bodyDoc: TiptapDoc | null;
    pinned: boolean;
    /** ISO timestamp when archived, null when active. */
    archivedAt: string | null;
    authorId: string;
    authorName: string;
    authorInitials: string;
    createdAt: string;
    updatedAt: string;
}

export type NoteSeverity = PatientNote['severity'];
export type NoteVisibility = PatientNote['visibility'];

interface NoteWriteBody {
    severity: NoteSeverity;
    title: string;
    /** Rich-editor content. The server derives the plain-text `body` from it. */
    bodyDoc?: TiptapDoc;
    /** Plain-text body for programmatic callers with no rich content. */
    body?: string;
    visibility?: NoteVisibility;
    pinned?: boolean;
    archived?: boolean;
    /** Set when created from a communication interaction (see EntryRowMenu's
     *  "Add patient note") - links the note back to its source entry. */
    communicationEntryId?: string;
}

export async function fetchNotes(
    patientId: string,
    includeArchived = false
): Promise<PatientNote[]> {
    const response = await apiV2.get<{ success: true; data: PatientNote[] }>(
        `/patients/${patientId}/notes`,
        includeArchived ? { params: { includeArchived: true } } : undefined
    );
    return response.data.data;
}

export async function createNote(patientId: string, body: NoteWriteBody): Promise<PatientNote> {
    const response = await apiV2.post<{ success: true; data: PatientNote }>(
        `/patients/${patientId}/notes`,
        body
    );
    return response.data.data;
}

export async function updateNote(
    patientId: string,
    noteId: string,
    body: Partial<NoteWriteBody>
): Promise<PatientNote> {
    const response = await apiV2.patch<{ success: true; data: PatientNote }>(
        `/patients/${patientId}/notes/${noteId}`,
        body
    );
    return response.data.data;
}

export async function deleteNote(patientId: string, noteId: string): Promise<void> {
    await apiV2.delete(`/patients/${patientId}/notes/${noteId}`);
}
